import React from 'react'
import { QueryResult } from '@apollo/client'
import {
  useAuth,
  useRemoveCompanyGroup,
  CompanyDetails,
  CompanyDetailsVariables,
  UserPermissions,
} from '@modmd/data'
import Button from 'components/Button'
import Datalist from 'components/Datalist'
import Icon from 'components/Icon'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useToaster } from 'utils/useToaster'
import { Dialog, DialogContentText } from 'components/Dialog'
import Card from 'components/Card'
import { CreateNewGroupDrawer } from 'sharedComponents/CreateNewGroupDrawer'
import { CompanyGroupDetail } from './CompanyGroupDetail'

export interface CompanyGroupsProps {
  companyQuery: QueryResult<CompanyDetails, CompanyDetailsVariables>
}

const SKELETON_ROWS = 10
const SKELETON_COLUMNS = 3

const CompanyGroups: React.VFC<CompanyGroupsProps> = ({ companyQuery }) => {
  const { hasPermission } = useAuth()
  const { setToastMessage } = useToaster()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const companyId = companyQuery?.data?.company?.id
  const [openGroupDetail, setOpenGroupDetail] = React.useState<{
    name: string
    id: string
    japaneseGroup: boolean
  } | null>(null)
  const [isOpenCreateNewGroup, setIsOpenCreateNewGroup] = React.useState(false)
  const [groupToRemove, setGroupToRemove] = React.useState<{
    id: string
    name: string
  } | null>(null)

  const handleOpenCreateNewGroup = React.useCallback(() => {
    setIsOpenCreateNewGroup(true)
  }, [])

  const handleCloseCreateNewGroup = React.useCallback(() => {
    setIsOpenCreateNewGroup(false)
  }, [])

  const [removeCompanyGroup, { loading: isRemoveCompanyGroupLoading }] = useRemoveCompanyGroup()

  const handleRemoveCompanyGroup = React.useCallback(async () => {
    try {
      await removeCompanyGroup({
        variables: {
          id: groupToRemove!.id,
        },
      })
      setToastMessage('Group successfully deleted', 'success')
      if (companyQuery.refetch) {
        void companyQuery.refetch()
      }
    } catch {
      // ignore
    }
    setGroupToRemove(null)
  }, [groupToRemove, removeCompanyGroup, setToastMessage, companyQuery])

  const handleNewGroupCreated = React.useCallback(
    async (groupId: string) => {
      if (companyId && companyQuery.refetch) {
        const { data } = await companyQuery.refetch({ id: companyId })
        const newGroup = data.company?.Groups?.find(({ id }) => id === groupId)
        if (newGroup) {
          handleCloseCreateNewGroup()
          setOpenGroupDetail(newGroup)
        }
      }
    },
    [companyId, companyQuery, handleCloseCreateNewGroup]
  )

  const handleGroupDetails = async (groupId: string) => {
    if (companyId && companyQuery.refetch) {
      const { data } = await companyQuery.refetch({ id: companyId })
      const newGroup = data.company?.Groups?.find(({ id }) => id === groupId)
      if (newGroup) setOpenGroupDetail(newGroup)
    }
  }

  const handleSetGroupName = React.useCallback(
    ({
      name,
      id,
      japaneseGroup = false,
    }: {
      name: string
      id: string
      japaneseGroup?: boolean
    }) => {
      setOpenGroupDetail({ name, id, japaneseGroup })
    },
    []
  )

  return (
    <Card mt="1rem">
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '900px' : '100%'}
        isOpen={!!openGroupDetail?.id}
        onDismiss={() => setOpenGroupDetail(null)}
      >
        {openGroupDetail && companyId && companyQuery.refetch && (
          <CompanyGroupDetail
            companyId={companyId}
            groupName={openGroupDetail.name}
            groupId={openGroupDetail.id}
            isJapaneseGroup={openGroupDetail.japaneseGroup}
            onClose={() => setOpenGroupDetail(null)}
            onGroupRefetch={(groupId: string) => handleGroupDetails(groupId)}
            onRefetch={() => companyQuery.refetch({ id: companyId })}
            onSetGroupName={handleSetGroupName}
          />
        )}
      </Dialog>
      <Datalist
        isLoading={companyQuery.loading}
        skeletonProps={{
          columns: SKELETON_COLUMNS,
          rows: SKELETON_ROWS,
        }}
        data={
          companyQuery.data?.company?.Groups?.map(({ id, name, japaneseGroup }) => ({
            id,
            name,
            japaneseGroup,
          })) || []
        }
        shouldRenderTableHeader={isDesktop}
        showActionIndicators
        header={
          <Datalist.Title
            rightContent={
              hasPermission([UserPermissions.CREATE_GROUP]) && (
                <Button
                  size="small"
                  marginLeft="auto"
                  onClick={handleOpenCreateNewGroup}
                  leftIcon={<Icon.Plus size="L" />}
                >
                  Add group
                </Button>
              )
            }
          >
            Groups
          </Datalist.Title>
        }
        renderTableHeader={() => (
          <Datalist.Row gridTemplateColumns="auto repeat(2, 1fr)">
            <Datalist.HeaderCell label="#" />
            <Datalist.HeaderCell label="Name" />
            {hasPermission([UserPermissions.DELETE_GROUP]) ? (
              <Datalist.HeaderCell label="Actions" />
            ) : null}
          </Datalist.Row>
        )}
        renderTableRow={({ id, name, japaneseGroup }) => (
          <Datalist.Row
            gridTemplateColumns="auto repeat(2, 1fr)"
            onClick={() =>
              setOpenGroupDetail({
                id: String(id),
                name: String(name),
                japaneseGroup: Boolean(japaneseGroup),
              })
            }
          >
            <Datalist.Cell label="#">{id}</Datalist.Cell>
            <Datalist.Cell label="Name">{name}</Datalist.Cell>
            {hasPermission([UserPermissions.DELETE_GROUP]) ? (
              <Datalist.Cell label="Actions">
                <Button
                  size="small"
                  colorVariant="danger"
                  appearance="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    setGroupToRemove({
                      id,
                      name,
                    })
                  }}
                >
                  <Icon.Trash />
                </Button>
              </Datalist.Cell>
            ) : null}
          </Datalist.Row>
        )}
      />
      {companyId && (
        <CreateNewGroupDrawer
          isOpen={isOpenCreateNewGroup}
          onDismiss={handleCloseCreateNewGroup}
          onGroupCreated={handleNewGroupCreated}
          companyId={companyId}
        />
      )}
      <Dialog
        onDismiss={() => setGroupToRemove(null)}
        isOpen={!!groupToRemove}
        variant="center"
        maxWidth={isDesktop ? '550px' : '100%'}
      >
        <Card>
          <Card.Title>Confirm delete group</Card.Title>
          <Card.Content>
            {groupToRemove?.name && (
              <DialogContentText>{`Are you sure to delete group ${groupToRemove.name}? Group will be permanently removed from company groups.`}</DialogContentText>
            )}
          </Card.Content>
          <Card.Actions>
            <Button appearance="ghost" onClick={() => setGroupToRemove(null)}>
              Cancel
            </Button>
            <Button
              colorVariant="danger"
              onClick={handleRemoveCompanyGroup}
              isFetching={isRemoveCompanyGroupLoading}
            >
              Delete
            </Button>
          </Card.Actions>
        </Card>
      </Dialog>
    </Card>
  )
}

export { CompanyGroups }
